import { UseMutationOptions, useQueryClient } from 'react-query'
import { useCallback } from 'react'
import { useToast } from '@chakra-ui/react'
import { SendMessageMutation, SendMessageMutationVariables } from '../api/gqlaxy/gql/generated/graphql'
import { graphqlGLClient } from '../api/gqlaxy/gql/queries/graphQLClient'
import { useSendMessageMutation } from '../api/gqlaxy/gql/generated/graphql-hooks'
import { defineMessages, useIntl } from 'react-intl'

interface ErrorPrompt {
  error: string
  message: string
}

interface Option<D, E, V> extends UseMutationOptions<D, E, V> {
  disableErrorPrompts?: boolean
  errorPrompts?: ErrorPrompt[]
}

export const MSG = defineMessages({
  errorSendingMessage: {
    id: 'useSendMessage.errorSendingMessage',
    defaultMessage: 'Error occurred while sending message'
  },
  messageSent: {
    id: 'useSendMessage.messageSent',
    defaultMessage: 'Message sent successfully'
  }
})

export const useSendMessage = (option: Option<SendMessageMutation, Error, SendMessageMutationVariables> = {}) => {
  const toast = useToast()
  const intl = useIntl()
  const client = graphqlGLClient({
    // endpoint: 'http://127.0.0.1:5001/mobile-staging-376418/us-central1/gqlactus' // local endpoint
  })

  const { disableErrorPrompts, errorPrompts, ...options } = option

  const handleError = useCallback(
    (...args) => {
      const [e] = args

      if (disableErrorPrompts) return

      const prompt = errorPrompts?.find(({ error }) => error === e.message)
      const message = prompt?.message ?? e.message

      toast({
        description: message,
        status: 'error',
        duration: 4000,
        position: 'top-right'
      })
    },
    [disableErrorPrompts, errorPrompts, options, toast]
  )

  const handleSettled = useCallback(
    (...args) => {
      const [_, error] = args

      if (error) {
        toast({
          description: intl.formatMessage(MSG.errorSendingMessage),
          status: 'error',
          duration: 4000,
          position: 'top-right'
        })
      } else {
        toast({
          description: intl.formatMessage(MSG.messageSent),
          status: 'success',
          duration: 4000,
          position: 'top-right'
        })
      }
    },
    [options, toast]
  )

  return useSendMessageMutation(client, {
    onError: handleError,
    onSettled: handleSettled,
    ...options
  })
}
